import { StyledCard } from 'components/Custom/card';
import { ScopeTable } from 'components/Custom/table';
import * as React from 'react';
import { DataContact } from 'store/vat/models/vatReportData';
import { VatRunData } from 'store/vat/models';
import { MenuSize, ViesStatus } from 'store/app/enums';
import {
  formatNumberWithLocale,
  getDefaultLocale,
} from 'utils/numberFormat';
import { StatusGetter } from 'utils/status-getter';
import { CardDescSpan } from 'components/Custom/spans';
import { Space } from 'antd';
import { VatCheckerUnavailableBanner } from 'pages/vies/temporarily-unavailable';
import { isVatCheckerDisabled } from 'utils/feature-flag-utils';
import i18n from 'i18next';

interface TableDataContact extends DataContact {
  key: number;
}
export const renderCyprusRecap = (
  contacts: DataContact[] | undefined,
  vatRunData: VatRunData,
  _menuSize: MenuSize,
  _showSideMenu: boolean,
  showRotateHint = false
) => {
  const locale = getDefaultLocale();
  const tableContacts: TableDataContact[] = [];
  contacts?.forEach((c, key) => tableContacts.push({ ...c, key }));
  const isEmptyRecap = tableContacts.length === 0;
  const columns = [
    {
      title: i18n.t('vat-report.member-state'),
      dataIndex: 'country',
    },
    {
      title: i18n.t('vat-report.prefix'),
      render: (_, record: DataContact) => {
        const vatNumber = record.taxNumber;
        const match = vatNumber?.match(/^[A-Za-z]+/);
        const prefix = match ? match[0] : '';
        return prefix;
      },
    },
    {
      title: i18n.t('vat-report.vat-registration-number'),
      render: (_, record: DataContact) => {
        const vatNumber = record.taxNumber;
        const taxNumber = vatNumber?.replace(/\D/g, '');
        return taxNumber;
      },
    },
    {
      title: i18n.t('vat-report.vat-verified'),
      dataIndex: 'viesStatus',
      render: (_, record: DataContact) => {
        return <StatusGetter status={ViesStatus[record.viesStatus]} noDraft />;
      },
    },
    {
      title: i18n.t('vat-report.goods-subtotal'),
      dataIndex: 'goodsSum',
      align: 'right',
      render: (_, record: DataContact) => {
        return formatNumberWithLocale(locale, record.goodsSum);
      },
    },
    {
      title: i18n.t('vat-report.triangular-transactions'),
      dataIndex: 'triangularTradeSum',
      align: 'right',
      render: (_, record: DataContact) => {
        return formatNumberWithLocale(locale, record.triangularTradeSum);
      },
    },
    {
      title: i18n.t('vat-report.services-subtotal'),
      dataIndex: 'servicesSum',
      align: 'right',
      render: (_, record: DataContact) => {
        return formatNumberWithLocale(locale, record.servicesSum);
      },
    },
  ];

  const renderHeaderComponent = () => {
    const totalGoods =
      contacts?.reduce((sum, item) => sum + item.goodsSum, 0) || 0;
    const totalServices =
      contacts?.reduce((sum, item) => sum + item.servicesSum, 0) || 0;
    const totalTriangular =
      contacts?.reduce((sum, item) => sum + item.triangularTradeSum, 0) || 0;

    return (
      <Space direction="horizontal" style={{ float: 'right' }}>
        <CardDescSpan
          style={{
            float: 'right',
            fontSize: 11,
          }}
        >
          {i18n.t('vat-report.totals-goods-subtotal')}{' '}
          <span
            style={{
              textAlign: 'end',
              fontWeight: 600,
            }}
          >
            {formatNumberWithLocale(locale, totalGoods)}
          </span>
          {'     '}
          {i18n.t('vat-report.services-subtotal')}{' '}
          <span
            style={{
              textAlign: 'end',
              fontWeight: 600,
            }}
          >
            {formatNumberWithLocale(locale, totalServices)}
          </span>
          {'     '}
          {i18n.t('vat-report.triangular-transactions')}{' '}
          <span
            style={{
              textAlign: 'end',
              fontWeight: 600,
            }}
          >
            {formatNumberWithLocale(locale, totalTriangular)}
          </span>
        </CardDescSpan>
      </Space>
    );
  };

  const renderMobileSummary = () => {
    const totalGoods =
      contacts?.reduce((sum, item) => sum + item.goodsSum, 0) || 0;
    const totalServices =
      contacts?.reduce((sum, item) => sum + item.servicesSum, 0) || 0;
    const totalTriangular =
      contacts?.reduce((sum, item) => sum + item.triangularTradeSum, 0) || 0;

    return (
      <div className="scope-pwa-recap-summary">
        <div className="scope-pwa-recap-summary__row">
          <span>{i18n.t('vat-report.totals-goods-subtotal')}</span>
          <strong>{formatNumberWithLocale(locale, totalGoods)}</strong>
        </div>
        <div className="scope-pwa-recap-summary__row">
          <span>{i18n.t('vat-report.services-subtotal')}</span>
          <strong>{formatNumberWithLocale(locale, totalServices)}</strong>
        </div>
        <div className="scope-pwa-recap-summary__row">
          <span>{i18n.t('vat-report.triangular-transactions')}</span>
          <strong>{formatNumberWithLocale(locale, totalTriangular)}</strong>
        </div>
      </div>
    );
  };

  const cardWidth = '100%';

  return (
    <>
      {showRotateHint && (
        <div className="scope-pwa-recap-rotate-hint">
          Turn your phone sideways to view tables more easily.
        </div>
      )}
      {isVatCheckerDisabled() && (
        <VatCheckerUnavailableBanner width={cardWidth} />
      )}
      <StyledCard //@ts-ignore
        width={cardWidth}
        style={{
          overflowY: showRotateHint ? 'auto' : 'hidden',
          overflowX: showRotateHint ? 'hidden' : 'auto',
        }}
      >
        <div
          style={{
            padding: '10px',
            paddingLeft: '5px',
            paddingTop: showRotateHint ? '24px' : '10px',
            margin: 'auto',
          }}
        >
          {showRotateHint ? (
            <div className="scope-pwa-recap-cards">
              <div className="scope-pwa-recap-cards__summary">{renderMobileSummary()}</div>
              {isEmptyRecap ? (
                <div className="scope-pwa-recap-cards__empty">
                  No data found for this period
                </div>
              ) : (
                tableContacts.map((record) => {
                  const vatNumber = record.taxNumber ?? '';
                  const match = vatNumber.match(/^[A-Za-z]+/);
                  const prefix = match ? match[0] : '';
                  const taxNumber = vatNumber.replace(/\D/g, '');
                  return (
                    <div className="scope-pwa-recap-card" key={record.key}>
                      <div className="scope-pwa-recap-card__row">
                        <span>{i18n.t('vat-report.member-state')}</span>
                        <strong>{record.country}</strong>
                      </div>
                      <div className="scope-pwa-recap-card__row">
                        <span>{i18n.t('vat-report.prefix')}</span>
                        <strong>{prefix}</strong>
                      </div>
                      <div className="scope-pwa-recap-card__row">
                        <span>{i18n.t('vat-report.vat-registration-number')}</span>
                        <strong>{taxNumber}</strong>
                      </div>
                      <div className="scope-pwa-recap-card__row">
                        <span>{i18n.t('vat-report.vat-verified')}</span>
                        <strong>
                          <StatusGetter status={ViesStatus[record.viesStatus]} noDraft />
                        </strong>
                      </div>
                      <div className="scope-pwa-recap-card__row">
                        <span>{i18n.t('vat-report.goods-subtotal')}</span>
                        <strong>{formatNumberWithLocale(locale, record.goodsSum)}</strong>
                      </div>
                      <div className="scope-pwa-recap-card__row">
                        <span>{i18n.t('vat-report.triangular-transactions')}</span>
                        <strong>
                          {formatNumberWithLocale(locale, record.triangularTradeSum)}
                        </strong>
                      </div>
                      <div className="scope-pwa-recap-card__row">
                        <span>{i18n.t('vat-report.services-subtotal')}</span>
                        <strong>{formatNumberWithLocale(locale, record.servicesSum)}</strong>
                      </div>
                    </div>
                  );
                })
              )}
              <div className="scope-pwa-recap-table-landscape">
                <ScopeTable
                  className={`recap-statement${isEmptyRecap ? ' recap-statement--empty' : ''}`}
                  originalDataSource={tableContacts} //@ts-ignore
                  tableDataSource={tableContacts}
                  pagination={true}
                  rowsPerPage={10}
                  enableColumnsCustomization={false}
                  rowHeight={35}
                  columns={columns}
                  transactionType={'RECAP'}
                  reportId={vatRunData.openDataLogId}
                  titleComponent={renderHeaderComponent()}
                  showSizeChanger={false}
                  getNewPageSize={() => {}}
                  scroll={{ x: isEmptyRecap ? '100%' : 'max-content' }}
                />
              </div>
            </div>
          ) : (
            <ScopeTable
              className={`recap-statement${isEmptyRecap ? ' recap-statement--empty' : ''}`}
              originalDataSource={tableContacts} //@ts-ignore
              tableDataSource={tableContacts}
              pagination={true}
              rowsPerPage={10}
              enableColumnsCustomization={false}
              rowHeight={35}
              columns={columns}
              transactionType={'RECAP'}
              reportId={vatRunData.openDataLogId}
              titleComponent={renderHeaderComponent()}
              showSizeChanger={false}
              getNewPageSize={() => {}}
              scroll={{ x: isEmptyRecap ? '100%' : 'max-content' }}
            />
          )}
        </div>
      </StyledCard>
    </>
  );
};
